import React, { useState, useEffect } from "react";
import Layout from '../pages/Layout';
import Advertise from "../pages/Advertise";

const Dti = () => {
  // State variables for DTI Ratio calculation
  const [monthlyDebt, setMonthlyDebt] = useState(0); // Total monthly debt payments
  const [monthlyIncome, setMonthlyIncome] = useState(0); // Gross monthly income
  const [dtiRatio, setDtiRatio] = useState(0); // Calculated DTI Ratio

  // Event handlers to update state values
  const handleMonthlyDebtChange = (e) => setMonthlyDebt(parseFloat(e.target.value) || 0);
  const handleMonthlyIncomeChange = (e) => setMonthlyIncome(parseFloat(e.target.value) || 0);

  // useEffect hook to calculate DTI Ratio when any input changes
  useEffect(() => {
    if (monthlyIncome !== 0) {
      const dti = (monthlyDebt / monthlyIncome) * 100;
      setDtiRatio(dti);
    } else {
      setDtiRatio(0);
    }

    // This will initialize or reinitialize all popovers on the page whenever the component renders
    const popoverTriggerList = [].slice.call(
      document.querySelectorAll('[data-bs-toggle="popover"]')
    );

    // Clean up any existing popover instances
    popoverTriggerList.forEach((popoverTriggerEl) => {
      const popover = window.bootstrap.Popover.getInstance(popoverTriggerEl); // Check if instance already exists
      if (popover) {
        popover.dispose(); // Dispose of existing popover
      }
    });

    // Re-initialize popovers
    popoverTriggerList.forEach((popoverTriggerEl) => {
      new window.bootstrap.Popover(popoverTriggerEl); // Re-initialize popovers
    });


  }, [monthlyDebt, monthlyIncome]);

  return (
    <Layout title="Debt-to-Income (DTI) Ratio Calculator" path="dti-ratio">
      <section className="section-padding" id="dti-ratio-calculator">
        <div className="container mt-5">
          <div className="row">
            {/* DTI Ratio Calculator Form */}
            <div className="col-lg-9 col-md-8 mb-4">
              <div
                className="p-4 border rounded shadow"
                style={{ border: "1px solid black" }}
              >
                <h3 className="text-center mb-4">Debt-to-Income (DTI) Ratio Calculator</h3>
                <div className="container">
                  <div className="row gy-3">
                    {/* Monthly Debt Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="monthlyDebt" className="form-label">
                        Total Monthly Debt Payments (₹)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Monthly Debt:</strong> The total amount of debt payments you make every month, such as credit card bills, loan repayments, and other financial obligations."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="monthlyDebt"
                        placeholder="Enter total monthly debt payments"
                        value={monthlyDebt || ""}
                        onChange={handleMonthlyDebtChange}
                      />
                    </div>

                    {/* Monthly Income Input */}
                    <div className="col-md-6 col-lg-4">
                      <label htmlFor="monthlyIncome" className="form-label">
                        Gross Monthly Income (₹)
                        <span
                    className="ms-2 text-muted"
                    data-bs-toggle="popover"
                    data-bs-html="true"
                    data-bs-placement="top"
                    data-bs-content="<strong>Gross Monthly Income:</strong> Your total monthly income before taxes and deductions, including salary, bonuses, rental income, and any other sources of income."
                    style={{ cursor: "pointer" }}
                  >
                    <i className="bi bi-info-circle"></i>
                  </span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        id="monthlyIncome"
                        placeholder="Enter gross monthly income"
                        value={monthlyIncome || ""}
                        onChange={handleMonthlyIncomeChange}
                      />
                    </div>
                  </div>

                  {/* Result Section */}
                  <div className="mt-4">
                    <div className="p-3 text-center">
                      <h6>
                        Debt-to-Income Ratio:{" "}
                        <span className="fw-bold ms-3">
                          {dtiRatio === 0 ? "0.00" : dtiRatio.toFixed(2)} %
                        </span>
                      </h6>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Advertisement Section */}
            <Advertise />
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="introduction-section" id="introduction">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="p-5">
                <h3 className="text-center mb-5">
                  Introduction to the Debt-to-Income (DTI) Ratio Calculator
                </h3>

                {/* What is a Debt-to-Income (DTI) Ratio Calculator? */}
                <h4 className="mt-3 mb-3">What is a Debt-to-Income (DTI) Ratio Calculator?</h4>
                <p>
                  A **Debt-to-Income (DTI) Ratio Calculator** helps determine the percentage of an individual's gross monthly income that goes towards paying monthly debt obligations. It is a key indicator used by lenders to assess whether an individual is likely to be able to repay a loan.
                </p>

                {/* Why Use a Debt-to-Income (DTI) Ratio Calculator? */}
                <h4 className="mt-4 mb-3">Why Use a Debt-to-Income (DTI) Ratio Calculator?</h4>
                <p>
                  This calculator is crucial for both individuals and lenders. For individuals, it provides insight into their ability to manage debt relative to their income. For lenders, it is an important factor in determining loan eligibility and potential borrower risk.
                </p>

                {/* Benefits of Using a Debt-to-Income (DTI) Ratio Calculator */}
                <h4 className="mt-4 mb-3">Benefits of Using a Debt-to-Income (DTI) Ratio Calculator</h4>
                <ul>
                  <li><strong>Useful for Determining Loan Eligibility:</strong> Lenders use the DTI ratio to decide whether an individual qualifies for a loan or mortgage.</li>
                  <li><strong>Helps Individuals Manage Debt Levels:</strong> Individuals can track their debt load and identify areas where they may need to adjust their spending or repayment plans.</li>
                  <li><strong>Assists Lenders in Assessing Borrower Risk:</strong> A higher DTI ratio may signal to lenders that an individual has a higher risk of defaulting on a loan.</li>
                </ul>

                {/* Suitable For */}
                <h4 className="mt-4 mb-3">Who Should Use a Debt-to-Income (DTI) Ratio Calculator?</h4>
                <p>
                  The Debt-to-Income (DTI) Ratio Calculator is suitable for:
                </p>
                <ul>
                  <li><strong>Banking Institutions:</strong> Banks and mortgage companies use this tool to evaluate the borrowing capacity of their clients.</li>
                  <li><strong>Mortgage Lenders:</strong> It helps in assessing whether a borrower can afford a mortgage based on their monthly income and debt payments.</li>
                  <li><strong>Financial Advisors:</strong> Advisors use the DTI ratio to guide clients on managing debt and improving their financial health.</li>
                </ul>

                {/* Additional Questions */}
                <h4 className="mt-4 mb-3">What is considered a good DTI ratio?</h4>
                <p>
                  A DTI ratio of 36% or lower is generally considered good by lenders, with 28% or less being ideal for mortgage approval.
                </p>

                <h4 className="mt-4 mb-3">How can I improve my DTI ratio?</h4>
                <p>
                  You can improve your DTI ratio by paying off existing debt, increasing your income, or reducing your monthly debt obligations.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default Dti;
